import { t } from "i18next";
import { Typography } from "@material-tailwind/react";
import Switch from "../../switch/Switch";
import { useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { api } from "../../../services/api/api";

export default function ReportTemplateForm({
  data,
  onChange,
  submitted,
  isEditing
}: {
  data?: any, // EasyTech.ReportTemplate + custom fields
  onChange: (name: string, value: any) => void,
  onSubmit: () => void,
  submitted?: boolean,
  isEditing?: boolean
}) {
  const [initialIsDefault] = useState<boolean>(data?.isDefault || false);
  const [templates, setTemplates] = useState<any[]>([]);

  useEffect(() => {
    if (isEditing && initialIsDefault) {
      api.get("admin/report-templates")
        .then(ret => {
          const list = ret as any[];
          // Filter out the current template being edited
          setTemplates(list.filter(t => t.id !== data?.id));
        })
        .catch(err => {
          console.error(err);
        });
    }
  }, [isEditing, initialIsDefault, data?.id]);

  const selectOptions = templates.map(tp => ({
    value: tp.id as string,
    label: tp.name
  }));

  const loadOptions = (inputValue: string, callback: (options: any[]) => void) => {
    const filtered = selectOptions.filter(i =>
      i.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered);
  };

  const selectedOption = selectOptions.find(opt => opt.value === data?.newDefaultTemplateId) || null;

  // Show selection component only if this template WAS default, and is now untoggled
  const showReplacementSelector = isEditing && initialIsDefault && !data?.isDefault;

  return (
    <>
      <div className="field">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("common:name", "Name")}
        </label>
        <input
          type="text"
          value={data?.name || ""}
          onChange={(e) => onChange("name", e.target.value)}
          className={`form-field focus:!border-blue-chronoflex-500 focus:!ring-blue-chronoflex-500 ${(submitted && !data?.name) ? "error" : ""}`}
          placeholder={t("settings:reports:templates:namePlaceholder", "Template Name")}
        />
      </div>

      <div className="field mt-6 flex !flex-row items-start justify-between">
        <div className="flex-1">
          <Typography variant="small" color="blue-gray" className="font-medium">
            {t("settings:reports:templates:defaultLabel", "Default")}
          </Typography>
          <Typography variant="small" color="gray" className="font-normal">
            {t("settings:reports:templates:defaultDescription", "this setting will prevail if another report was already set to default")}
          </Typography>
        </div>
        <div className="flex-shrink-0">
          <Switch
            checked={data?.isDefault || false}
            onChange={(val) => {
              onChange("isDefault", val);
              if (val) {
                // If toggled back on, clean up chosen replacement default
                onChange("newDefaultTemplateId", null);
              }
            }}
          />
        </div>
      </div>

      {showReplacementSelector && (
        <div className="flex flex-col gap-2 mt-6">
          <Typography variant="h6" className="text-xs font-semibold text-gray-600 uppercase">
            {t("settings:reports:templates:delete:defaultReportLabel", "Default report")}
          </Typography>
          <div className="p-4 border border-orange-200 bg-orange-50 rounded-lg">
            <div className="flex items-start gap-2 mb-4">
              <ExclamationTriangleIcon className="h-5 w-5 text-orange-700 mt-0.5 flex-shrink-0" />
              <div className="flex flex-col">
                <Typography className="text-sm font-bold text-orange-800">
                  {t("settings:reports:templates:delete:replaceDefault", "Replace the default report")}
                </Typography>
                <Typography className="text-xs text-orange-700">
                  {t("settings:reports:templates:delete:replaceDefaultDesc", "Before deleting the default report you must configure another one instead.")}
                </Typography>
              </div>
            </div>

            <div className="w-full select-white-bg">
              <style>{`
                .select-white-bg .react-select__control {
                  background-color: #ffffff !important;
                }
              `}</style>
              <AsyncSelect
                cacheOptions
                defaultOptions={selectOptions}
                loadOptions={loadOptions}
                value={selectedOption}
                onChange={(opt: any) => {
                  onChange("newDefaultTemplateId", opt ? opt.value : null);
                }}
                classNamePrefix="react-select"
                placeholder={t("settings:reports:templates:delete:selectDefault", "Select a default report")}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: "#ffffff",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    backgroundColor: "#ffffff",
                  })
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
